import { useState } from 'react';
import { Button } from '@/components/ui-kit/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui-kit/card';
// import PageBreadcrumb from '@/components/breadcrumb/breadcrumb';
import { getProviderConfig, formatProviderName } from '@/modules/gpt-chats/utils/model-selector';
import { AIModelsTable } from '@/modules/gpt-chats/components/aimodels/aimodel-table/aimodel-table';
// import { BREADCRUMB_CUSTOM_TITLES } from '@/constant/breadcrumb-custom-title';
import { CustomModelAddKeyModal } from '@/modules/gpt-chats/components/aimodels/modals/aimodel-addkey-modal-custom';
import { ModelAddKeyModal } from '@/modules/gpt-chats/components/aimodels/modals/aimodel-addkey-modal/aimodel-addkey-modal';
import {
  createCustomProvider,
  getProviderDisplayName,
  PROVIDER_LOGOS,
  ProviderToPlatformMap,
  ProviderType,
  ServicePlatform,
} from '@/modules/gpt-chats/utils/aimodel-provider.utils';
import {
  useAIModelsQueryParams,
  useGetModels,
  useSeedModelsByProvider,
  useSeedProviders,
} from '@/modules/gpt-chats/hooks/use-aimodel';
import { getPng } from '@/modules/gpt-chats/constants';
import { Plus } from 'lucide-react';
import { Pagination } from '@/components/ui-kit/pagination';
import { AIModelsFilterToolbar } from '@/modules/gpt-chats/components/aimodels/aimodel-selectedpage-filter-toolbar';

type AIModelSelectedPageProps = {
  provider: string;
};

import { useTranslation } from 'react-i18next';

export const AIModelSelectedPage = ({ provider }: AIModelSelectedPageProps) => {
  const { t } = useTranslation();
  const { data: providers } = useSeedProviders();
  // const providers = normalizeProviderList(providersRaw) ?? [];

  const servicePlatform = ProviderToPlatformMap[provider.toLowerCase()] as ServicePlatform;

  const description =
    providers && Array.isArray(providers) && provider
      ? provider.toLowerCase() === ProviderType.CUSTOM
        ? createCustomProvider().Description
        : (providers.find((p) => p.Provider && p.Provider.toLowerCase() === provider.toLowerCase())
            ?.Description ?? '')
      : '';

  const { data: seedModels, isLoading: isSeedLoading } = useSeedModelsByProvider(provider);
  // const seedModels = normalizeSeedModelInfoList(seedModelsRaw) ?? [];
  const baseUrl = seedModels && seedModels.length > 0 ? seedModels[0].DefaultBaseUrl || '' : '';

  type ModelOption = { model: string; goodName: string };
  const modelOptions: ModelOption[] =
    seedModels?.map((m) => ({
      model: m.Model,
      goodName: m.ModelGoodName ?? m.Model,
    })) ?? [];

  const {
    queryParams: { search, page, page_size },
    setQueryParams,
  } = useAIModelsQueryParams();

  const {
    data: models,
    isLoading: isModelsLoading,
    isFetching: isModelsFetching,
  } = useGetModels({
    provider: provider.toLowerCase(),
    search: search || null,
    page: page,
    page_size: page_size,
  });

  const loading = isModelsLoading || isModelsFetching;
  const totalCount: number = models?.total ?? 0;
  const currentPage: number = page ?? models?.page ?? 1;
  const providerKey = provider.toLowerCase();
  const logoSrc = PROVIDER_LOGOS[providerKey] || getPng(providerKey);

  // BREADCRUMB_CUSTOM_TITLES['/ai/models'] = '';
  // BREADCRUMB_CUSTOM_TITLES[`/ai/models/${provider}`] = getProviderDisplayName(provider);

  const [addKeyModalOpen, setAddKeyModalOpen] = useState(false);

  return (
    <div>
      {/* <div className="flex flex-row gap-2">
        <PageBreadcrumb breadcrumbIndex={2} />
      </div> */}

      <div className="my-4 flex flex-row">
        <div
          className={`flex h-12 w-12 items-center justify-center rounded-sm border p-${logoSrc ? '0' : '2'}`}
        >
          {logoSrc ? (
            <img src={logoSrc} alt={provider} width={64} height={64} className="object-contain" />
          ) : (
            (() => {
              const ProviderIcon = getProviderConfig(formatProviderName(provider)).icon;
              return <ProviderIcon className="aspect-square w-8" />;
            })()
          )}
        </div>

        <div className="flex flex-col">
          <h4 className="text-lg font-semibold md:text-xl">{getProviderDisplayName(provider)}</h4>
          <p className="text-medium-emphasis">{description}</p>
        </div>
      </div>

      <div className="flex flex-row gap-5">
        <Card className="flex w-full flex-col">
          <CardHeader className="mb-0 flex w-full flex-row justify-between">
            <CardTitle className="text-lg font-semibold text-high-emphasis">
              {t('SELF_OWNED_MODEL')}
            </CardTitle>
            <Button
              variant="outline"
              className="px-2 py-1 sm:px-4 sm:py-2"
              onClick={() => {
                if (!isSeedLoading) setAddKeyModalOpen(true);
              }}
            >
              <Plus className="mr-2 h-4 w-4" />
              {t('ADD_MODEL')}
            </Button>
          </CardHeader>

          <CardContent>
            <div className="mb-4">
              <AIModelsFilterToolbar />
            </div>

            <AIModelsTable
              custom={provider.toLowerCase() === ProviderType.CUSTOM}
              models={models?.models || []}
              isLoading={loading}
            />

            {!loading && totalCount > page_size && (
              <div className="mt-4 flex items-center justify-end">
                <Pagination
                  page={currentPage - 1}
                  pageSize={page_size}
                  pageSizeOptions={[page_size]}
                  totalCount={totalCount}
                  onChange={(nextPageZeroBased) =>
                    setQueryParams((prev) => ({
                      ...prev,
                      page: nextPageZeroBased + 1,
                    }))
                  }
                  onPageSizeChange={(newPageSize) =>
                    setQueryParams((prev) => ({
                      ...prev,
                      page_size: newPageSize,
                      page: 1,
                    }))
                  }
                />
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {provider.toLowerCase() === ProviderType.CUSTOM ? (
        <CustomModelAddKeyModal
          addKeyModalOpen={addKeyModalOpen}
          setAddKeyModalOpen={setAddKeyModalOpen}
        />
      ) : (
        <ModelAddKeyModal
          key={baseUrl}
          provider={provider}
          modelOptions={modelOptions}
          baseUrl={baseUrl}
          servicePlatform={servicePlatform}
          addKeyModalOpen={addKeyModalOpen}
          setAddKeyModalOpen={setAddKeyModalOpen}
        />
      )}
    </div>
  );
};
